/*
 * This service handles session storage using httpOnly secure cookies
 * by communicating with the backend auth service.
 */

import { DiscoveryApi } from '@mainstage/core-plugin-api';

export interface CookieSessionService {
  saveSession<T>(storageKey: string, sessionData: T): Promise<void>;
  loadSession<T>(storageKey: string): Promise<T | undefined>;
  removeSession(storageKey: string): Promise<void>;
}

export class DefaultCookieSessionService implements CookieSessionService {
  private readonly discoveryApi: DiscoveryApi;

  constructor(discoveryApi: DiscoveryApi) {
    this.discoveryApi = discoveryApi;
  }

  private getAuthBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('auth');
  }

  async saveSession<T>(storageKey: string, sessionData: T): Promise<void> {
    const baseUrl = await this.getAuthBaseUrl();

    const response = await fetch(`${baseUrl}/session-cookie/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies in the request
      body: JSON.stringify({
        storageKey,
        sessionData: this.serializeSession(sessionData),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Failed to save session: ${errorData.error || response.statusText}`,
      );
    }
  }

  async loadSession<T>(storageKey: string): Promise<T | undefined> {
    const baseUrl = await this.getAuthBaseUrl();

    const response = await fetch(
      `${baseUrl}/session-cookie/load/${encodeURIComponent(storageKey)}`,
      {
        method: 'GET',
        credentials: 'include', // Include cookies in the request
      },
    );

    if (response.status === 404) {
      return undefined; // Session not found
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Failed to load session: ${errorData.error || response.statusText}`,
      );
    }

    const data = await response.json();
    return this.deserializeSession<T>(data.sessionData);
  }

  async removeSession(storageKey: string): Promise<void> {
    const baseUrl = await this.getAuthBaseUrl();

    const response = await fetch(
      `${baseUrl}/session-cookie/remove/${encodeURIComponent(storageKey)}`,
      {
        method: 'DELETE',
        credentials: 'include', // Include cookies in the request
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Failed to remove session: ${errorData.error || response.statusText}`,
      );
    }
  }

  private serializeSession<T>(sessionData: T): any {
    return JSON.parse(
      JSON.stringify(sessionData, (_key, value) => {
        if (value instanceof Set) {
          return {
            __type: 'Set',
            __value: Array.from(value),
          };
        }
        return value;
      }),
    );
  }

  private deserializeSession<T>(sessionData: any): T {
    return JSON.parse(JSON.stringify(sessionData), (_key, value) => {
      if (value?.__type === 'Set') {
        return new Set(value.__value);
      }
      return value;
    });
  }
}
